<!-- Copyright 2022, Common Good Learning Tools LLC -->
<template>
<k-dialog
	v-model="dialog_open"
	max-width="600"
	initial-focus-selector=".k-copy-folder-structure-unit-select"
	persistent
	scrollable
>
	<k-card>
		<k-card-title style="border-bottom: 1px solid #999">Copy Folder Structure from Another Unit</k-card-title>

		<k-card-text class="mt-3" style="font-size: 16px">
			<div class="mb-3">Choose the unit whose folder structure you’d like to copy into <b>{{unit.title}}</b>. Only the folders will be copied; resources and lessons will not be included.</div>

			<k-autocomplete
				v-model="source_course_code"
				label="Collection"
				:items="course_options"
				:loading="loading_courses"
				placeholder="Choose a collection…"
				class="mb-3"
				background-color="#fff"
				outlined
				dense
				hide-details
			/>

			<k-autocomplete
				v-model="source_unit_id"
				label="Unit"
				:items="source_unit_options"
				:loading="loading_source_collection"
				:disabled="!source_course_code"
				placeholder="Choose a unit…"
				class="k-copy-folder-structure-unit-select"
				background-color="#fff"
				outlined
				dense
				hide-details
			/>
		</k-card-text>

		<k-card-actions class="pa-3" style="border-top: 1px solid #999">
			<k-spacer />

			<k-btn text @click="$emit('dialog_cancel')">Cancel</k-btn>

			<k-btn
				:disabled="!source_unit_id"
				color="primary"
				@click="copy_folder_structure"
			>
				Copy Folder Structure
			</k-btn>
		</k-card-actions>
	</k-card>
</k-dialog>
</template>

<script>
import { mapState } from 'vuex'

export default {
	name: 'CopyFolderStructureDialog',

	props: {
		collection: { type: Object, required: true },
		unit: { type: Object, required: true },
		save_unit_edits: { type: Function, required: true },
	},

	data() { return {
		dialog_open: true,
		source_course_code: this.collection.course_code,
		source_unit_id: '',
		loading_courses: false,
		loading_source_collection: false,
	}},

	computed: {
		...mapState(['all_courses']),

		course_options() {
			return this.all_courses
				.filter(lp => lp.lp_id > 0 && lp.title != '' && lp.user_can_view_lp())
				.map(lp => ({value: lp.course_code, text: lp.title}))
				.sort((a, b) => U.natural_sort(this.strip_leading_icon(a.text), this.strip_leading_icon(b.text)))
		},

		source_collection() {
			return this.all_courses.find(lp => lp.course_code == this.source_course_code)
		},

		source_unit_options() {
			if (!this.source_collection || !this.source_collection.fully_loaded) return []

			return this.source_collection.units
				.filter(u => !(this.source_collection.lp_id == this.collection.lp_id && u.lp_unit_id == this.unit.lp_unit_id))
				.map(u => ({value: u.lp_unit_id, text: `${u.display_number ? u.display_number + ': ' : ''}${u.title}`}))
		},
	},

	watch: {
		source_course_code() {
			this.source_unit_id = ''
			this.load_source_collection()
		},
	},

	created() {
		this.loading_courses = true
		this.$store.dispatch('get_all_courses', 'initial').finally(() => { this.loading_courses = false })

		this.load_source_collection()
	},

	methods: {
		// some collection titles are typed with a leading emoji (e.g. as a personal organizational marker); strip it for this list
		strip_leading_icon(title) {
			return title.replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '')
		},

		load_source_collection() {
			if (!this.source_course_code) return

			let lp = this.all_courses.find(c => c.course_code == this.source_course_code)
			if (lp && lp.fully_loaded) return

			this.loading_source_collection = true
			this.$store.dispatch('get_learning_progression', this.source_course_code).finally(() => {
				this.loading_source_collection = false
			})
		},

		copy_folder_structure() {
			let source_unit = this.source_collection.units.find(u => u.lp_unit_id == this.source_unit_id)
			if (!source_unit) return

			let n_folders_copied = 0

			let copy_children = (source_parent_id, target_parent_id) => {
				let children = source_unit.resource_tree.folders.filter(f => f.parent_folder_id == source_parent_id && !f.tcc_folder_id)

				for (let folder of children) {
					let new_folder = this.unit.create_resource_folder({
						title: folder.title,
						color: folder.color,
						parent_folder_id: target_parent_id,
						seq: folder.seq,
					})
					++n_folders_copied

					copy_children(folder.folder_id, new_folder.folder_id)
				}
			}

			copy_children('top', 'top')

			this.save_unit_edits().then(() => {
				this.$inform(`${n_folders_copied} ${U.ps('folder', n_folders_copied)} copied`)
			})

			this.$emit('dialog_cancel')
		},
	},
}
</script>

<style lang="scss">
</style>
